import {
    createHotpatch,
    TimeZoneSupportStatus
} from "@clhbid/canadian-time-zone-hotpatch";
import { Temporal } from "temporal-polyfill";

// The Canadian permanent-time rules live in the shared package, which owns the
// rule table, the stale-host detection and the approved English labels. This
// is the one place LiveAuction binds it, to the polyfilled Temporal the app
// already ships, without touching any global.
const {
    inspectHostSupport,
    inspectTimeZoneSupport,
    toCorrectedInstant,
    toCorrectedZonedTime,
    toTimeZoneLabel
} = createHotpatch({ temporal: Temporal });

// Telemetry probes the same bound hotpatch the app formats with, so it
// measures the Temporal implementation visitors actually run.
export { inspectHostSupport };

// A zone the host doesn't recognize can't be formatted, so public times fall
// back to Alberta time rather than a neutral zone like UTC: nearly all
// visitors are in Alberta, so its local time is the one they expect to read.
export const fallbackTimeZone = "America/Edmonton";

export interface IDisplayZone {
    // The zone to format in: on a stale host, the rule's fixed-offset zone.
    timeZoneId: string;
    // The approved short label to show in place of the host's zone name.
    label?: string;
}

// A zone the host doesn't recognize falls back to Alberta time, matched
// case-insensitively by the package so this only guards genuinely unknown
// identifiers. A `rule_outdated` zone is recognized, and the host knows more
// than the package, so it is kept and the package defers to the host.
const governedOrFallbackTimeZone = (timeZone: string): string =>
    inspectTimeZoneSupport(timeZone).status === TimeZoneSupportStatus.unknown
        ? fallbackTimeZone
        : timeZone;

// Resolves the zone an instant should be displayed in, and its approved label
// when the package has one.
export const resolveDisplayZone = (
    instant: Temporal.Instant,
    timeZone: string
): IDisplayZone => {
    const timeZoneId = governedOrFallbackTimeZone(timeZone);
    const input = { instant: instant.toString(), timeZoneId };

    return {
        timeZoneId: toCorrectedZonedTime(input).timeZoneId,
        label: toTimeZoneLabel(input)?.short
    };
};

// Resolves the wall time an editable field should show for an instant, in the
// zone `resolveDisplayZone` picked, so a stale host never shows an hour out.
export const resolveWallTime = (
    instant: Temporal.Instant,
    timeZone: string
): Temporal.PlainDateTime => {
    const { timeZoneId } = resolveDisplayZone(instant, timeZone);
    return instant.toZonedDateTimeISO(timeZoneId).toPlainDateTime();
};

// Resolves the instant an edited wall time means, or `null` when the zone
// skips or repeats it: `disambiguation: "reject"` never silently guesses.
export const resolveInstant = (
    wallTime: Temporal.PlainDateTime,
    timeZone: string
): Temporal.Instant | null => {
    const timeZoneId = governedOrFallbackTimeZone(timeZone);

    try {
        const { instant } = toCorrectedInstant({
            wallTime: wallTime.toString(),
            timeZoneId,
            disambiguation: "reject"
        });
        return Temporal.Instant.from(instant);
    } catch (error) {
        if (error instanceof RangeError) return null;
        throw error;
    }
};
